/**
 * 스크롤 추적
 *
 * 스크롤 깊이(scroll_depth) 이벤트 수집 (25%, 50%, 75%, 90% 기준)
 * - 각 임계값 페이지당 1회만 발행
 * - throttle 적용 (100ms)
 */

import type { LambAnalytics } from '../core/sdk';

const SCROLL_THRESHOLDS = [25, 50, 75, 90] as const;
const THROTTLE_MS = 100;

/** 스크롤 깊이 추적기 */
export class ScrollTracker {
  private sdk: LambAnalytics;
  private active = false;
  private firedThresholds: Set<number> = new Set();
  private scrollHandler: (() => void) | null = null;
  private throttleTimer: ReturnType<typeof setTimeout> | null = null;
  private isThrottled = false;

  constructor(sdk: LambAnalytics) {
    this.sdk = sdk;
  }

  start(): void {
    if (this.active || typeof window === 'undefined') return;
    this.active = true;
    this.firedThresholds.clear();

    this.scrollHandler = () => this.onScroll();
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  stop(): void {
    if (!this.active || typeof window === 'undefined') return;
    this.active = false;

    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
    if (this.throttleTimer !== null) {
      clearTimeout(this.throttleTimer);
      this.throttleTimer = null;
    }
    this.isThrottled = false;
  }

  /** 페이지 전환 시 임계값 리셋 */
  resetThresholds(): void {
    this.firedThresholds.clear();
  }

  private onScroll(): void {
    if (this.isThrottled) return;
    this.isThrottled = true;

    this.throttleTimer = setTimeout(() => {
      this.isThrottled = false;
      this.checkThresholds();
    }, THROTTLE_MS);
  }

  private checkThresholds(): void {
    if (!this.active) return;

    const depth = this.getScrollDepth();

    for (const threshold of SCROLL_THRESHOLDS) {
      if (depth >= threshold && !this.firedThresholds.has(threshold)) {
        this.firedThresholds.add(threshold);
        this.sdk.track('scroll', {
          scroll_depth: threshold,
        });
      }
    }
  }

  private getScrollDepth(): number {
    if (typeof document === 'undefined' || typeof window === 'undefined') return 0;

    const docHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight,
    );
    const viewportHeight = window.innerHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (docHeight <= viewportHeight) return 100;

    const scrollableHeight = docHeight - viewportHeight;
    if (scrollableHeight <= 0) return 100;

    return Math.round((scrollTop / scrollableHeight) * 100);
  }
}
